import { useEffect, useState } from 'react';
import authService from '@/services/AuthService';
import * as dbConstants from '@dao/dbConstants';
import { LogOut, User } from 'lucide-react';
import { _t } from '@/i18n/t';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';

import { getSessionProfileImage, hydrateSessionProfileImage } from '@/shared/utils/sessionProfileImage';
import type { RootState } from './types';

type Props = {
  auth: RootState['auth'];
  isCollapsed: boolean;
  onExpandSidebar: () => void;
  onLogout: () => void;
  onNavigateProfile: () => void;
};

const SidebarProfile = ({ auth, isCollapsed, onExpandSidebar, onLogout, onNavigateProfile }: Props) => {
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const userId = auth?.user?.id;
  const userProfilePicture = auth?.user?.profilePicture;

  useEffect(() => {
    let isActive = true;

    const resolveProfilePicture = async () => {
      if (!userId || !userProfilePicture) {
        setProfileImageUrl('');
        return;
      }

      const cachedImage = getSessionProfileImage(userId, userProfilePicture);
      if (cachedImage) {
        setProfileImageUrl(cachedImage);
        return;
      }

      try {
        const sessionImage = await hydrateSessionProfileImage(userId, userProfilePicture);
        if (isActive) {
          setProfileImageUrl(sessionImage);
        }
      } catch {
        if (isActive) {
          setProfileImageUrl('');
        }
      }
    };

    void resolveProfilePicture();

    return () => {
      isActive = false;
    };
  }, [userId, userProfilePicture]);

  const initials = `${auth?.user?.firstName?.[0] || ''}${auth?.user?.lastName?.[0] || ''}`.toUpperCase() || 'U';
  const fullName = `${auth?.user?.firstName || ''} ${auth?.user?.lastName || ''}`.trim() || _t('sidebar.user');
  const profileImageAlt = _t('sidebar.profileImageAlt', { name: fullName });

  return (
    <DropdownMenu open={profileMenuOpen} onOpenChange={setProfileMenuOpen}>
      <DropdownMenuTrigger asChild>
        <button
          onClick={(event) => {
            if (!isCollapsed) return;
            event.preventDefault();
            onExpandSidebar();
          }}
          className={`w-full bg-transparent flex items-center gap-[0.44rem] h-sidebar-profile hover:bg-nav-activeBg transition-colors ${
            isCollapsed ? 'justify-center px-0' : 'pl-3 pr-4'
          }`}
          title={isCollapsed ? fullName : undefined}
        >
          <div className="h-nav-avatar w-nav-avatar rounded-full overflow-hidden bg-nav-avatarBg text-nav-avatarText flex items-center justify-center text-body-xs font-medium flex-shrink-0">
            {profileImageUrl ? (
              <img src={profileImageUrl} alt={profileImageAlt} className="h-full w-full object-cover" />
            ) : (
              initials
            )}
          </div>
          {!isCollapsed && (
            <div className="min-w-0 text-left">
              <div className="text-body leading-tight font-medium text-nav-textStrong truncate">{fullName}</div>
              <div className="text-body-sm leading-tight text-nav-heading truncate mt-px">{auth?.user?.email}</div>
            </div>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="start"
        sideOffset={12}
        className="mb-nav-menu-popover-offset rounded-md border border-nav-border bg-card py-1"
      >
        {authService.hasPermission(dbConstants.PERMISSION_USER_PROFILE_READ) && (
          <DropdownMenuItem onSelect={onNavigateProfile} className="gap-2 px-3 py-2 text-nav-textStrong">
            <User className="h-nav-icon w-nav-icon" />
            {_t('sidebar.profile')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onLogout} variant="destructive" className="gap-2 px-3 py-2">
          <LogOut className="h-nav-icon w-nav-icon" />
          {_t('sidebar.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default SidebarProfile;
